import { useState, useCallback, useRef } from "react";
import { ToolContainer, FileDropZone, DownloadButton, downloadBlob, ResultPreview } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useFreemium } from "@/hooks/useFreemium";
import { loadFFmpeg, fetchFile, checkFileSize, ffmpegOutputToBlob, resetFFmpegEngine } from "@/lib/ffmpegEngine";
import { normalizeAudioLoudness, NORMALIZE_TARGETS, normalizeOutput, probeDuration } from "./operations";
import { AudioProcessingProgress, showAudioError, FileSizeNotice, useAudioPipelineStep } from "./uiHelpers";
import type { FFmpeg } from "@ffmpeg/ffmpeg";

const TOOL_INFO_CONTENT = {
  what: `Raises or lowers the whole file by one constant amount so its overall loudness reaches a target, using the same loudness standard (EBU R128) broadcasters and streaming platforms target. Useful for a recording where volume drifts up and down, or before uploading to a platform with its own loudness target. Runs entirely in your browser — the audio never leaves your device.`,
  how: `Upload your audio file and click Normalize — it analyzes the file and evens out the loudness automatically.`,
  tip: `This targets a broadcast-standard loudness level, which is a bit different from simply "making everything as loud as possible" (peak normalization) — it's built to make speech and music sound consistently loud without clipping or feeling overly compressed.`,
};

export default function AudioNormalizer() {
  const { inPipeline, intermediate } = useAudioPipelineStep();
  const [file, setFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [result, setResult] = useState<Blob | null>(null);
  const [targetId, setTargetId] = useState("podcast");
  const [summary, setSummary] = useState("");
  const [duration, setDuration] = useState<number | null>(null);
  // Inside a bundle the limiter is on, so a "podcast" target is actually reached even when a few peaks are high.
  const [limiter, setLimiter] = useState(inPipeline);
  const { isPro, isLoading, isAdmin } = useFreemium();
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    setFile(f); setResult(null); setDuration(null);
    probeDuration(f).then(setDuration);
  }, []);

  const sizeCheck = file ? checkFileSize([file], isPro || isLoading, isAdmin) : null;

  const stop = () => {
    abortRef.current?.abort();
    if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
  };

  const normalize = async () => {
    if (!file) return;
    setProcessing(true);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setStatusMessage("Loading audio engine...");
      const ffmpeg = await loadFFmpeg((msg) => setStatusMessage(msg));
      ffmpegRef.current = ffmpeg;

      setStatusMessage("Reading audio file...");
      const ext = file.name.match(/\.([^.]+)$/)?.[1] || "mp3";
      await ffmpeg.writeFile(`input.${ext}`, await fetchFile(file));

      // Two passes (measure, then apply exactly the gain needed) so quiet and loud files land on the same level.
      const target = NORMALIZE_TARGETS.find((t) => t.id === targetId) ?? NORMALIZE_TARGETS[0];
      setStatusMessage("Measuring and normalizing volume...");
      const out = normalizeOutput(file, intermediate, targetId, duration);
      const fmtOut = out.format;
      const r = await normalizeAudioLoudness(ffmpeg, `input.${ext}`, `output.${fmtOut.value}`, target, setProgress, controller.signal, out.codec, limiter);
      const data = r.data;
      const g = `${r.gainDb >= 0 ? "+" : ""}${r.gainDb.toFixed(1)} dB`;
      let text: string;
      if (target.lufs === null) {
        text = `Loudest point ${r.inputPeak?.toFixed(1)} dB → ${r.outputPeak !== null ? r.outputPeak.toFixed(1) : target.peak} dB (${g}).`;
      } else {
        text = `Loudness ${r.inputLufs!.toFixed(1)} LUFS → ${r.outputLufs !== null ? r.outputLufs.toFixed(1) : target.lufs} LUFS (${g}), measured on the saved file.`;
        if (r.limited) text += limiter ? ` Even with the peak limiter on, the recording could not reach ${target.lufs} LUFS.` : ` The target is ${target.lufs}, but the loud peaks stop it going louder without passing ${target.peak} dB.`;
      }
      if (r.gainDb > 12) text += ` This boosts a quiet recording by ${r.gainDb.toFixed(0)} dB, so background noise gets louder too.`;
      if (out.note) text += ` ${out.note}`;
      setSummary(text);

      setResult(ffmpegOutputToBlob(data, fmtOut.mime));
      toast.success("Volume normalized!");
    } catch (err) {
      showAudioError(err);
    } finally {
      if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
      setProcessing(false);
    }
  };

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept="audio/*" maxSize={500} onFiles={handleFiles} label="Drop an audio file" description="Even out volume levels" />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card"><p className="text-sm font-medium">{file.name}</p></div>
          {sizeCheck && <FileSizeNotice check={sizeCheck} isPro={isPro} />}
          <div className="space-y-2">
            <p className="text-sm font-medium">Target</p>
            <div className="flex flex-wrap gap-2">
              {NORMALIZE_TARGETS.map((t) => (
                <Button key={t.id} size="sm" variant={targetId === t.id ? "default" : "outline"} onClick={() => { setTargetId(t.id); setResult(null); }} title={t.hint}>{t.label}</Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{NORMALIZE_TARGETS.find((t) => t.id === targetId)?.hint}</p>
            <label className="flex min-h-[44px] cursor-pointer items-start gap-3 py-2 text-sm">
              <input type="checkbox" className="mt-0.5 h-6 w-6 shrink-0 cursor-pointer" checked={limiter} onChange={(e) => { setLimiter(e.target.checked); setResult(null); }} data-testid="norm-limiter" />
              <span>Limit loud peaks so the loudness target can be reached<span className="block text-xs text-muted-foreground">Turns the volume up as far as the target needs and catches the few peaks that would clip. Not used for the peak target.</span></span>
            </label>
          </div>
          {processing && <AudioProcessingProgress progress={progress} statusMessage={statusMessage} onStop={stop} />}
          {result && summary && <p className="text-sm text-muted-foreground" data-testid="norm-summary">{summary}</p>}
          {result && <ResultPreview blob={result} />}
          <div className="flex flex-wrap gap-3">
            {result ? (
              <>
                <DownloadButton blob={result} fileName={`normalized_${file.name.replace(/\.[^.]+$/, `.${normalizeOutput(file, intermediate, targetId, duration).format.value}`)}`} />
                <Button variant="outline" onClick={() => { setFile(null); setResult(null); }}>New</Button>
              </>
            ) : (
              <>
                <Button onClick={normalize} disabled={processing || sizeCheck?.severity !== "ok"}>{processing ? "Normalizing..." : "Normalize Volume"}</Button>
                <Button variant="outline" onClick={() => setFile(null)} disabled={processing}>Reset</Button>
              </>
            )}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
